import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FaUserTie, FaCheck, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import {
  LoadingScreen,
  InvitationErrorScreen,
  ExistingApplicationScreen,
  SuccessScreen,
} from '../components/pro/signup/SignupStatusScreens';
import {
  InvitationBanner,
  UserInviteBanner,
  FoundingAdvisorPromo,
  ReferrerBanner,
} from '../components/pro/signup/SignupBanners';
import { SignupStep1Account } from '../components/pro/signup/SignupStep1Account';
import { SignupStep2Professional } from '../components/pro/signup/SignupStep2Professional';
import { SignupStep3Rates } from '../components/pro/signup/SignupStep3Rates';
import { SignupSafeguards } from '../components/pro/signup/SignupSafeguards';
import { BrandLogo } from '../components/BrandLogo';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function ProfessionalSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const refCode = searchParams.get('ref');

  const [invitationInfo, setInvitationInfo] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(!!token || !!refCode);
  const [invitationError, setInvitationError] = useState('');
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [userInviteInfo, setUserInviteInfo] = useState(null);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    professional_type: '',
    company: '',
    specialties: [],
    bio: '',
    location: '',
    website: '',
    certifications: [],
    years_experience: '',
    hourly_rate: '',
    offers_free_consultation: false,
    referral_code: refCode || '',
    jurisdiction: '',
    license_number: '',
    licensing_body: '',
    business_email: '',
    fee_disclosure: '',
    conflicts_of_interest: '',
    offers_free_education: false,
    offers_paid_services: false,
    standards_accepted: false
  });
  const safeguardsComplete = formData.standards_accepted && formData.jurisdiction.trim()
    && formData.conflicts_of_interest.trim() && (!formData.offers_paid_services || formData.fee_disclosure.trim());
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [existingApplicationError, setExistingApplicationError] = useState(false);

  useEffect(() => {
    if (token) {
      verifyInvitation();
    }
    if (refCode) {
      verifyReferralCode();
    }
  }, [token, refCode]);

  const verifyReferralCode = async () => {
    try {
      // First, try to verify as a user invite token
      const userInviteResponse = await axios.get(`${API}/professional-invites/verify/${refCode}`);
      if (userInviteResponse.data.valid) {
        setUserInviteInfo(userInviteResponse.data);
        // Pre-fill form with invite data
        setFormData(prev => ({
          ...prev,
          name: userInviteResponse.data.professional_name || prev.name,
          referral_code: refCode
        }));
        setLoadingInvitation(false);
        return;
      }
    } catch (err) {
      // Not a user invite, try professional referral
    }

    try {
      // Try professional-to-professional referral
      const response = await axios.get(`${API}/professionals/referral/verify/${refCode}`);
      setReferrerInfo(response.data);
    } catch (err) {
      // Ignore — no valid referral
    }
    setLoadingInvitation(false);
  };

  const verifyInvitation = async () => {
    try {
      const response = await axios.get(`${API}/directory/invitation/${token}`);
      setInvitationInfo(response.data);
      // Pre-fill form with invitation data
      if (response.data.profile) {
        setFormData(prev => ({
          ...prev,
          name: response.data.profile.name || '',
          email: response.data.profile.email || '',
          professional_type: response.data.profile.professional_type || ''
        }));
      }
    } catch (err) {
      setInvitationError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoadingInvitation(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addSpecialty = () => {
    if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty.trim()]
      }));
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty)
    }));
  };

  const addCertification = () => {
    if (newCertification.trim() && !formData.certifications.includes(newCertification.trim())) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, newCertification.trim()]
      }));
      setNewCertification('');
    }
  };

  const removeCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c !== cert)
    }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!formData.professional_type || !formData.bio) {
      setError('Please select your profession type and add a bio');
      return false;
    }
    if (formData.bio.length < 50) {
      setError('Please write a more detailed bio (at least 50 characters)');
      return false;
    }
    setError('');
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        professional_type: formData.professional_type,
        company: formData.company || null,
        specialties: formData.specialties,
        bio: formData.bio,
        location: formData.location || null,
        website: formData.website || null,
        certifications: formData.certifications,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        offers_free_consultation: formData.offers_free_consultation,
        referral_code: formData.referral_code || null,
        jurisdiction: formData.jurisdiction || null,
        license_number: formData.license_number || null,
        licensing_body: formData.licensing_body || null,
        business_email: formData.business_email || null,
        fee_disclosure: formData.fee_disclosure || null,
        conflicts_of_interest: formData.conflicts_of_interest || null,
        offers_free_education: formData.offers_free_education,
        offers_paid_services: formData.offers_paid_services,
        standards_accepted: formData.standards_accepted
      };

      let response;
      if (token) {
        // Complete invited signup
        response = await axios.post(`${API}/directory/signup/complete/${token}`, payload);
      } else {
        // Regular self-signup
        response = await axios.post(`${API}/directory/signup`, payload);
      }

      // If this was a user invite (via ref parameter), track the completion
      if (userInviteInfo && refCode && response.data?.profile_id) {
        try {
          await axios.post(`${API}/professional-invites/complete/${refCode}`, null, {
            params: { professional_id: response.data.profile_id }
          });
        } catch (trackErr) {
          console.error('Error tracking referral completion:', trackErr);
          // Don't fail the signup if tracking fails
        }
      }

      setSubmitSuccess(true);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Registration failed. Please try again.';
      // Check if it's an "already exists" error
      if (errorMessage.includes('already exists') || errorMessage.includes('already applied')) {
        setExistingApplicationError(true);
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInvitation) return <LoadingScreen />;
  if (invitationError) return <InvitationErrorScreen message={invitationError} />;
  if (existingApplicationError) {
    return (
      <ExistingApplicationScreen
        onTryDifferent={() => {
          setExistingApplicationError(false);
          setError('');
          setStep(1);
        }}
      />
    );
  }
  if (submitSuccess) return <SuccessScreen token={token} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-testid="professional-signup-page">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <BrandLogo className="w-8 h-8" testId="pro-signup-logo" />
              <span className="text-xl font-bold text-white">Legacy Forge Family</span>
            </Link>
            <Link
              to="/login"
              className="text-white/80 hover:text-white text-sm"
            >
              Already have an account? Log in
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {invitationInfo && <InvitationBanner invitationInfo={invitationInfo} />}
          {userInviteInfo && <UserInviteBanner userInviteInfo={userInviteInfo} />}
          <FoundingAdvisorPromo />

          {/* Progress Steps */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step >= s
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/10 text-white/50'
                  }`}
                >
                  {step > s ? <FaCheck /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-blue-600' : 'bg-white/10'}`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center space-x-8 text-sm text-white/60 mb-8">
            <span className={step >= 1 ? 'text-white' : ''}>Account</span>
            <span className={step >= 2 ? 'text-white' : ''}>Professional Info</span>
            <span className={step >= 3 ? 'text-white' : ''}>Rates & Finish</span>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-white">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaUserTie className="text-2xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Professional Signup</h1>
                  <p className="text-blue-100">Join our network of trusted family advisors</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {referrerInfo && <ReferrerBanner referrerInfo={referrerInfo} />}

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {step === 1 && (
                <SignupStep1Account
                  formData={formData}
                  handleChange={handleChange}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  token={token}
                />
              )}

              {step === 2 && (
                <SignupStep2Professional
                  formData={formData}
                  setFormData={setFormData}
                  handleChange={handleChange}
                  newSpecialty={newSpecialty}
                  setNewSpecialty={setNewSpecialty}
                  addSpecialty={addSpecialty}
                  removeSpecialty={removeSpecialty}
                />
              )}

              {step === 3 && (
                <>
                  <SignupStep3Rates
                    formData={formData}
                    handleChange={handleChange}
                    newCertification={newCertification}
                    setNewCertification={setNewCertification}
                    addCertification={addCertification}
                    removeCertification={removeCertification}
                  />
                  <SignupSafeguards formData={formData} handleChange={handleChange} />
                </>
              )}

              {/* Navigation Buttons */}
              <div className="flex space-x-4 mt-8">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    <FaArrowLeft className="inline mr-2" />
                    Back
                  </button>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    data-testid="next-step-btn"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting || !safeguardsComplete}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {submitting ? 'Creating Profile...' : 'Complete Registration'}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Info Note */}
          <p className="text-center text-white/60 text-sm mt-6">
            {token
              ? 'Complete your profile to start connecting with families.'
              : 'Registration is free. Your profile will appear in our directory after an LFF administrator verifies it.'}
          </p>
        </div>
      </div>
    </div>
  );
}
